import '@ant-design/v5-patch-for-react-19';
import { python } from "@codemirror/lang-python";
import CodeMirror from '@uiw/react-codemirror';
import { Button, Card, Splitter } from "antd";
import { useCallback, useEffect, useState } from "react";
import Markdown from "react-markdown";
import api from "../api/axios.js";
import { useChatbot } from "../contexts/ChatContext.jsx";
import { useEvent } from "../contexts/EventContext.jsx";


function ProgrammingAssignment({ learningJourneyId, view }) {
    const [description, setDescription] = useState(null);
    const [code, setCode] = useState("");
    const [output, setOutput] = useState("");
    const { emitEvent } = useEvent();

    const { setShowChatbot } = useChatbot();

    useEffect(() => {
        setShowChatbot(true);  // Enable chatbot in this view
        return () => setShowChatbot(false);  // Disable when leaving
    }, [setShowChatbot]);

    const fetchExercise = useCallback(async () => {
        try {
            const { data } = await api.post(`/workspace/programming/`, {
                learning_journey_id: learningJourneyId
            });
            setDescription(data.description);
            setCode(data.code);
        } catch (error) {
            console.error("Failed to load reading:", error);
        }
    }, [learningJourneyId]);

    useEffect(() => {
        fetchExercise();
    }, [view, fetchExercise]);

    const handleSubmit = async () => {
        setOutput("");

        try {
            const response = await api.post(`/workspace/programming/submit/`, {
                learning_journey_id: learningJourneyId,
                code: code,
            });

            if (!response.ok) {
                throw new Error("Failed to execute code");
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split("\n").filter((line) => line.trim() !== "");
                lines.forEach((line) => {
                    const parsed = JSON.parse(line);
                    if (parsed.type === "pytest") {
                        setOutput((prevOutput) => prevOutput + parsed.delta); // Append pytest output
                    } else if (parsed.type === "control") {
                        emitEvent(parsed);
                    }
                });
            }
        } catch (error) {
            console.error("Failed to execute code:", error);
            setOutput("Failed to execute code. Please try again.");
        }
    };

    return (
        <Splitter>
            <Splitter.Panel collapsible style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "5px" }}>
                <Card
                    title="Exercise"
                    style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                    styles={{
                        body: {
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            overflow: "auto",
                        }
                    }}
                >
                    <Markdown>{description}</Markdown>
                </Card>
            </Splitter.Panel>
            <Splitter.Panel collapsible>
                <Splitter layout="vertical">
                    <Splitter.Panel
                        collapsible
                        style={{ display: "flex", flexDirection: "column", flex: 1, padding: "5px" }}
                    >
                        <Card
                            title="Code"
                            style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden"
                            }}
                            styles={{
                                body: {
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    padding: 0,
                                    overflow: "auto"
                                }
                            }}
                        >
                            {/* CodeMirror Wrapper */}
                            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "auto" }}>
                                <CodeMirror
                                    value={code}
                                    extensions={[python()]}
                                    style={{ flex: 1, height: "100%", width: "100%" }}
                                    onChange={(value, viewUpdate) => {
                                        setCode(value);
                                    }}
                                />
                            </div>
                        </Card>

                        <div
                            style={{
                                padding: "10px",
                                textAlign: "right",
                                background: "#fff",
                                borderTop: "1px solid #d9d9d9", // Match Card border
                                borderBottomLeftRadius: 8, // Add bottom rounded corners here
                                borderBottomRightRadius: 8,
                            }}
                        >
                            <Button type="primary" onClick={handleSubmit}>Submit</Button>
                        </div>
                    </Splitter.Panel>
                    <Splitter.Panel collapsible style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "5px" }}>
                        <Card
                            title="Run"
                            style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column"
                            }}
                            styles={{
                                body: {
                                    flex: 1,
                                    padding: 0,
                                    display: "flex",
                                    overflow: "auto"
                                }
                            }}
                        >
                            <div
                                style={{
                                    backgroundColor: "#000", // Black background like a terminal
                                    color: "#0f0", // Green text like classic terminals
                                    fontFamily: "monospace",
                                    flex: 1,
                                    overflowY: "auto",
                                    whiteSpace: "pre-wrap",
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: "0 0 5px 5px",
                                }}
                            >
                                <pre style={{ margin: 0, flex: 1, padding: "10px 10px 10px 15px" }}>
                                    <code>{output}</code>
                                </pre>
                            </div>
                        </Card>
                    </Splitter.Panel>
                </Splitter>
            </Splitter.Panel>
        </Splitter>
    );
}

export default ProgrammingAssignment;